import { useEffect, useState } from "react";
import { X, Globe, DownloadSimple, Copy, Check, CircleNotch, ArrowsClockwise } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin & client modal: generates two launch image variants (laptop+phone and laptop-only)
 * from a URL, lets user download either, plus copy social captions for X / LinkedIn / Instagram.
 *
 * Live preview shows both variants side-by-side once captured. URL is pre-filled from
 * the project's link deliverables but admin can override before regenerating.
 *
 * `isAdmin` switches the endpoint family — admins hit /admin/projects/... so cross-client
 * access works; clients hit /projects/... which enforces ownership server-side.
 */
export function LaunchImageModal({ open, projectId, onClose, isAdmin = true }) {
  const basePath = isAdmin
    ? `/admin/projects/${projectId}/launch-image`
    : `/projects/${projectId}/launch-image`;
  const [info, setInfo] = useState(null);           // {candidate_urls, default_url, captions, ...}
  const [url, setUrl] = useState("");
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [images, setImages] = useState({
    laptop_phone: null,
    laptop_only: null,
    instagram_square: null,
    instagram_story: null,
  }); // blob URLs
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (!open || !projectId) return;
    setLoadingInfo(true);
    setImages({ laptop_phone: null, laptop_only: null, instagram_square: null, instagram_story: null });
    api.get(`${basePath}-info`)
      .then(({ data }) => {
        setInfo(data);
        setUrl(data.default_url || "");
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load info"))
      .finally(() => setLoadingInfo(false));
    return () => {
      setImages((prev) => {
        Object.values(prev).forEach((u) => u && URL.revokeObjectURL(u));
        return { laptop_phone: null, laptop_only: null, instagram_square: null, instagram_story: null };
      });
    };
  }, [open, projectId, basePath]);

  const generateBoth = async () => {
    const target = (url || "").trim();
    if (!target) return toast.error("URL is required");
    setGenerating(true);
    // Revoke any previous blobs first
    Object.values(images).forEach((u) => u && URL.revokeObjectURL(u));
    setImages({ laptop_phone: null, laptop_only: null, instagram_square: null, instagram_story: null });
    try {
      const variants = ["laptop_phone", "laptop_only", "instagram_square", "instagram_story"];
      const results = await Promise.all(
        variants.map((variant) =>
          api.post(basePath, { url: target, variant }, { responseType: "blob" })
        )
      );
      const next = {};
      variants.forEach((v, i) => { next[v] = URL.createObjectURL(results[i].data); });
      setImages(next);
      toast.success("Launch images ready");
    } catch (e) {
      let detail = formatApiErrorDetail(e.response?.data?.detail);
      // axios with responseType:"blob" returns Blob on error too — read it as text to extract detail
      if (!detail && e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const parsed = JSON.parse(txt);
          detail = formatApiErrorDetail(parsed.detail) || parsed.detail;
        } catch (_) { /* keep fallback */ }
      }
      toast.error(detail || "Couldn't capture site. The URL may be slow, blocked, or unreachable — try again or use a different URL.");
    } finally {
      setGenerating(false);
    }
  };

  const download = (variant) => {
    const blobUrl = images[variant];
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    const safe = (info?.project_title || "launch").replace(/[^a-z0-9]+/gi, "-").slice(0, 40).toLowerCase();
    a.download = `${safe}-${variant}.png`;
    a.click();
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Copied");
      setTimeout(() => setCopiedKey(null), 1600);
    } catch (_) {
      window.prompt("Copy this:", text);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" data-testid="launch-image-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-[#262626] bg-[#0a0a0a]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Launch image</div>
            <div className="font-display text-xl mt-1">Vision brought to life</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* URL input */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
              Website URL <span className="text-[#FF2D2D]">*</span>
            </label>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 flex items-center gap-2 bg-[#050505] border border-[#262626] focus-within:border-[#FF2D2D] px-3">
                <Globe size={14} className="text-[#a3a3a3]" />
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  data-testid="launch-url-input"
                  placeholder="https://yourclient.com"
                  className="flex-1 bg-transparent py-2 text-white text-sm focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={generateBoth}
                disabled={generating || loadingInfo || !url.trim()}
                data-testid="launch-generate-btn"
                className="btn-primary px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
              >
                {generating ? <><CircleNotch size={12} className="animate-spin" /> Capturing…</>
                  : (images.laptop_phone || images.instagram_square) ? <><ArrowsClockwise size={12} weight="bold" /> Regenerate</>
                  : <>Generate</>}
              </button>
            </div>
            {info?.candidate_urls?.length > 1 && (
              <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] text-[#a3a3a3]">
                <span>Detected on this project:</span>
                {info.candidate_urls.map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setUrl(u)}
                    data-testid={`launch-candidate-${u}`}
                    className="underline hover:text-white truncate max-w-[200px]"
                  >
                    {u}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Variants */}
          {generating && (
            <div className="border border-[#262626] bg-[#050505] py-12 text-center" data-testid="launch-generating">
              <CircleNotch size={20} className="animate-spin text-[#FF2D2D] mx-auto" />
              <div className="text-xs text-[#a3a3a3] mt-3 uppercase tracking-wider">Rendering 4 formats…</div>
              <div className="text-[10px] text-[#666] mt-1">Landscape + Instagram square + story · takes 15-25 seconds</div>
            </div>
          )}
          {!generating && (images.laptop_phone || images.laptop_only || images.instagram_square || images.instagram_story) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="launch-results">
              <VariantCard
                label="Landscape · Laptop + Phone"
                src={images.laptop_phone}
                onDownload={() => download("laptop_phone")}
                aspect="aspect-[16/9]"
                testId="variant-laptop-phone"
              />
              <VariantCard
                label="Landscape · Laptop only"
                src={images.laptop_only}
                onDownload={() => download("laptop_only")}
                aspect="aspect-[16/9]"
                testId="variant-laptop-only"
              />
              <VariantCard
                label="Instagram · Square (1:1)"
                src={images.instagram_square}
                onDownload={() => download("instagram_square")}
                aspect="aspect-square"
                testId="variant-instagram-square"
              />
              <VariantCard
                label="Instagram · Story (9:16)"
                src={images.instagram_story}
                onDownload={() => download("instagram_story")}
                aspect="aspect-[9/16]"
                testId="variant-instagram-story"
              />
            </div>
          )}

          {/* Social captions */}
          {info?.captions && (
            <div className="border-t border-[#262626] pt-5" data-testid="launch-captions">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D] mb-3">Share copy</div>
              <div className="space-y-3">
                <CaptionBlock label="X / Twitter" text={info.captions.twitter} copied={copiedKey === "twitter"} onCopy={() => copy("twitter", info.captions.twitter)} />
                <CaptionBlock label="LinkedIn"    text={info.captions.linkedin} copied={copiedKey === "linkedin"} onCopy={() => copy("linkedin", info.captions.linkedin)} />
                <CaptionBlock label="Instagram"   text={info.captions.instagram} copied={copiedKey === "instagram"} onCopy={() => copy("instagram", info.captions.instagram)} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VariantCard({ label, src, onDownload, testId, aspect = "aspect-[16/9]" }) {
  return (
    <div className="border border-[#262626] bg-[#050505]" data-testid={testId}>
      <div className={`${aspect} bg-black overflow-hidden flex items-center justify-center`}>
        {src && <img src={src} alt={label} className="w-full h-full object-contain block" />}
      </div>
      <div className="p-3 flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wider text-[#a3a3a3]">{label}</span>
        <button
          type="button"
          onClick={onDownload}
          data-testid={`${testId}-download`}
          className="btn-primary px-3 py-1.5 text-[11px] uppercase tracking-wider flex items-center gap-1.5"
        >
          <DownloadSimple size={12} weight="bold" /> Download
        </button>
      </div>
    </div>
  );
}

function CaptionBlock({ label, text, copied, onCopy }) {
  return (
    <div className="border border-[#262626] bg-[#050505] p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">{label}</div>
        <button
          type="button"
          onClick={onCopy}
          className="text-[10px] uppercase tracking-wider text-[#a3a3a3] hover:text-white flex items-center gap-1"
        >
          {copied ? <><Check size={11} weight="bold" /> Copied</> : <><Copy size={11} /> Copy</>}
        </button>
      </div>
      <pre className="text-xs text-white whitespace-pre-wrap font-sans leading-relaxed">{text}</pre>
    </div>
  );
}
